// redux/slices/messageTemplatesSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getJwtToken } from "../../Utils/authStorage";
import { getServerUrl } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
    url,
    method = "GET",
    headers = {},
    data,
}) => {
    const jwt = await getJwtToken();

    try {
        const config = {
            url: `${getServerUrl()}${url}`,
            method,
            headers: {
                "Content-Type": "application/json",
                ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
                ...headers,
            },
        };

        if (data !== undefined && data !== null) {
            config.data = data;
        }

        const response = await axios(config);

        return response.data;
    } catch (error) {
        throw {
            status: error?.response?.status,
            data: error?.response?.data,
            message:
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Request failed",
        };
    }
};

const getErrorMessage = (error, fallback) =>
    error?.data?.error ||
    error?.data?.message ||
    error?.message ||
    fallback;

/* ===================== THUNKS ===================== */

export const fetchTemplates = createAsyncThunk(
    "messageTemplates/fetchTemplates",
    async (uid, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error("UID не передан");

            const data = await axiosRequest({
                url: `/messageTemplates/${uid}`,
            });

            return Array.isArray(data) ? data : [];
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Fetch Message Templates failed")
            );
        }
    }
);

export const createTemplate = createAsyncThunk(
    "messageTemplates/createTemplate",
    async (templateData, { rejectWithValue }) => {
        try {
            const data = await axiosRequest({
                url: "/messageTemplates",
                method: "POST",
                data: templateData,
            });

            return data?.template || data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Create Message Template failed")
            );
        }
    }
);

export const updateTemplate = createAsyncThunk(
    "messageTemplates/updateTemplate",
    async ({ id, templateData }, { rejectWithValue }) => {
        try {
            if (!id) throw new Error("ID шаблона не передан");

            const data = await axiosRequest({
                url: `/messageTemplates/${id}`,
                method: "PUT",
                data: templateData,
            });

            return data?.template || data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Update Message Template failed")
            );
        }
    }
);

export const deleteTemplate = createAsyncThunk(
    "messageTemplates/deleteTemplate",
    async (payload, { rejectWithValue }) => {
        try {
            const id =
                typeof payload === "object"
                    ? payload?.id
                    : payload;

            if (!id) {
                throw new Error("ID шаблона не передан");
            }

            await axiosRequest({
                url: `/messageTemplates/${id}`,
                method: "DELETE",
            });

            return id;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(
                    error,
                    "Delete Message Template failed"
                )
            );
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    templates: [],
    status: "idle",
    loading: false,
    error: null,
};

/* ===================== SLICE ===================== */

const messageTemplatesSlice = createSlice({
    name: "messageTemplates",
    initialState,
    reducers: {
        clearMessageTemplatesError: (state) => {
            state.error = null;
        },

        resetMessageTemplatesStatus: (state) => {
            state.status = "idle";
            state.loading = false;
            state.error = null;
        },

        clearMessageTemplates: (state) => {
            state.templates = [];
            state.status = "idle";
            state.loading = false;
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder

            // FETCH

            .addCase(fetchTemplates.pending, (state) => {
                state.status = "loading";
                state.loading = true;
                state.error = null;
            })

            .addCase(fetchTemplates.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.loading = false;
                state.templates = Array.isArray(action.payload)
                    ? action.payload
                    : [];
            })

            .addCase(fetchTemplates.rejected, (state, action) => {
                state.status = "failed";
                state.loading = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Fetch Message Templates failed";
            })

            // CREATE

            .addCase(createTemplate.pending, (state) => {
                state.status = "loading";
                state.loading = true;
                state.error = null;
            })

            .addCase(createTemplate.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.loading = false;

                if (action.payload) {
                    state.templates.push(action.payload);
                }
            })

            .addCase(createTemplate.rejected, (state, action) => {
                state.status = "failed";
                state.loading = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Create Message Template failed";
            })

            // UPDATE

            .addCase(updateTemplate.pending, (state) => {
                state.status = "loading";
                state.loading = true;
                state.error = null;
            })

            .addCase(updateTemplate.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.loading = false;

                if (!action.payload?.id) return;

                const index = state.templates.findIndex(
                    (template) =>
                        String(template.id) === String(action.payload.id)
                );

                if (index !== -1) {
                    state.templates[index] = action.payload;
                }
            })

            .addCase(updateTemplate.rejected, (state, action) => {
                state.status = "failed";
                state.loading = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Update Message Template failed";
            })

            // DELETE

            .addCase(deleteTemplate.pending, (state) => {
                state.status = "loading";
                state.loading = true;
                state.error = null;
            })

            .addCase(deleteTemplate.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.loading = false;

                state.templates = state.templates.filter(
                    (template) =>
                        String(template.id) !== String(action.payload)
                );
            })

            .addCase(deleteTemplate.rejected, (state, action) => {
                state.status = "failed";
                state.loading = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Delete Message Template failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const {
    clearMessageTemplatesError,
    resetMessageTemplatesStatus,
    clearMessageTemplates,
} = messageTemplatesSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectMessageTemplatesState = (state) =>
    state.messageTemplates;

export const selectMessageTemplates = (state) =>
    state.messageTemplates?.templates || [];

export const selectMessageTemplatesStatus = (state) =>
    state.messageTemplates?.status || "idle";

export const selectMessageTemplatesLoading = (state) =>
    !!state.messageTemplates?.loading;

export const selectMessageTemplatesError = (state) =>
    state.messageTemplates?.error || null;

/* ===== Алиас для совместимости ===== */

export { fetchTemplates as fetchMessageTemplates };

export default messageTemplatesSlice.reducer;